import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Banknote } from 'lucide-react';
import { z } from 'zod';
import { FormDialog, FormSection, FormTextareaField } from '@/components/common/FormDialog';
import { WarningNotice } from '@/components/common/WarningNotice';
import { DONATION_COMMENT_MAX_LENGTH } from '../constants/marketPromotion.constants';
import { useDisburseDonationBatch } from '../hooks/useMpP2Mutations';
import type { DonationBatch } from '../types/marketPromotionP2.types';
import { getBatchNumber } from '../utils/batchNumber';

const BATCH_LABEL_SEPARATOR = ' - ';

const disburseFormSchema = z.object({
  comment: z.string().trim().min(1, 'Write a comment').max(DONATION_COMMENT_MAX_LENGTH),
});

type DisburseFormValues = z.infer<typeof disburseFormSchema>;

interface DisburseDonationBatchDialogProps {
  batch: DonationBatch;
  onClose: () => void;
}

export function DisburseDonationBatchDialog({ batch, onClose }: DisburseDonationBatchDialogProps) {
  const form = useForm<DisburseFormValues>({
    resolver: zodResolver(disburseFormSchema),
    defaultValues: { comment: '' },
  });
  const comment = useWatch({ control: form.control, name: 'comment' });
  const disburse = useDisburseDonationBatch(onClose);
  const batchLabel = [`Batch #${String(getBatchNumber(batch))}`, batch.name]
    .filter(Boolean)
    .join(BATCH_LABEL_SEPARATOR);

  function handleDisburse(values: DisburseFormValues) {
    disburse.mutate({ batchId: batch.batchId, comment: values.comment.trim() });
  }

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      icon={Banknote}
      title="Disburse Batch"
      description={batchLabel}
      form={form}
      isPending={disburse.isPending}
      isEditMode={false}
      submitLabel="Disburse"
      isSubmitDisabled={comment.trim() === ''}
      onSubmit={handleDisburse}
    >
      <WarningNotice>This batch will be marked as paid. You cannot undo this.</WarningNotice>
      <FormSection>
        <FormTextareaField
          control={form.control}
          name="comment"
          label="Comment"
          placeholder="Write how the money was paid"
          maxLength={DONATION_COMMENT_MAX_LENGTH}
          isFullRow
        />
      </FormSection>
    </FormDialog>
  );
}
